import {
  FeatureName,
  Media,
  MediaPickerComponent,
  MediaType,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentLocation,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { Text } from 'react-native-paper';
import { array, string, object as yupObject } from 'yup';
import { Spacing } from '../../styles';
import { formStyles } from '../../styles/forms';
import { Colors } from '../../themes';
import { tokens } from '../crop-circle/tokens';
import { ControlledTextInput, SaveFormBottom } from '../forms';
import { GroupSelector } from '../group';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { HorizontalMediaGallery } from '../media';
import { BottomComponent } from '../navigation';
import { useSlideLayoutSafe } from '../slide-layout';
import { NewAsset as NewAssetOld } from './newAsset-old';

export type NewAssetStyles = {
  container: ViewStyle;
  contentContainer: ViewStyle;
  imageGalleryContainer?: ViewStyle;
};

export type NewAssetProps = {
  onCancel: () => void;
  mediaPicker: MediaPickerComponent;
  topComponentBar: any;
  styles: StyleSheet.NamedStyles<NewAssetStyles>;
  onSaved: (assetId: string) => void;
  bottomBar?: BottomComponent;
  selectedGroups?: Array<{ id: string; name: string }>;
  onGroupsChange?: (groups: Array<{ id: string; name: string }>) => void;
  saveRef?: React.RefObject<(() => void) | undefined>;
  onCanSaveChange?: (canSave: boolean) => void;
  onSelectorEdit?: () => void;
};

export const NewAsset = (props: NewAssetProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleRedesignEnabled) {
    return <NewAssetBase {...props} />;
  }

  return <NewAssetOld {...props} />;
};
  
export const NewAssetBase = (props: NewAssetProps) => {
  const MediaPicker = props.mediaPicker;

  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const { tagId, tagCode } = params;
  const [uploading, setUploading] = useState(false);
  const { mutate, isPending: isLoading } = useCustomMutation({
    query: createAsset,
  });
  const { createGroup } = useCreateGroup();
  const [assetMedia, setAssetMedia] = useState<string[]>([]);
  const workspace = useCurrentWorkspace();
  const { getLocation } = useCurrentLocation();
  const slideLayout = useSlideLayoutSafe();

  const { groups } = useAllWorkspaceGroups(workspace?.id);
  const [internalSelectedGroups, setInternalSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const selectedGroups = props.selectedGroups ?? internalSelectedGroups;
  const setSelectedGroups = props.onGroupsChange ?? setInternalSelectedGroups;

  const addMedia = (newMediaItem: Media) => {
    if (!newMediaItem) return;
    setAssetMedia((prevState) => [...prevState, newMediaItem.id]);
  };

  const deleteMedia = (mediaId: string) => {
    setAssetMedia((prevState) => prevState.filter((id) => id !== mediaId));
  };

  const formSchema = yupObject({
    name: string().required(),
    description: string().optional(),
    details: string().optional(),
    groupIds: array().of(string().required()).default([]),
    tagCode: string().optional(),
  });

  type NewAssetFormValues = {
    name: string;
    description?: string;
    details?: string;
    groupIds: string[];
    tagCode?: string;
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<NewAssetFormValues>({
    resolver: yupResolver<NewAssetFormValues>(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
      details: '',
      groupIds: [],
      tagCode: tagCode ? String(tagCode) : undefined,
    },
  });

  const canSave = useMemo(() => {
    return isDirty && isValid && !uploading && !isLoading;
  }, [isValid, uploading, isLoading, isDirty]);

  useEffect(() => {
    props.onCanSaveChange?.(canSave);
  }, [canSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmitRef = useRef<() => void>(() => {});

  const onSubmit = async (data: any) => {
    let location;
    if (tagId) {
      location = await getLocation();
    }
    delete data.tagCode;

    // For the asset creation, we will treat the first media item as the cover image and the rest as additional media. 
    const coverImageId = assetMedia.length > 0 ? assetMedia[0] : undefined;
    const mediaIds = assetMedia.length > 1 ? assetMedia.slice(1) : undefined;

    const input: any = {
      ...data,
      groupIds: selectedGroups.map((group) => group.id),
      ...(tagId && { tagId }),
      ...(location && { lastLocation: location }),
      workspaceId: workspace.id,
      mediaIds: mediaIds,
      coverImageId: coverImageId,
    };
    mutate(
      { input: input },
      {
        onSettled: (response: any) => {
          const assetId = response?.asset?.addAsset?.id;
          if (assetId) props.onSaved(assetId);
        },
      }
    );
  };

  useEffect(() => {
    onSubmitRef.current = handleSubmit(onSubmit);
    if (props.saveRef) {
      (props.saveRef as React.MutableRefObject<(() => void) | undefined>).current = handleSubmit(onSubmit);
    }
  });

  const onResetForm = () => {
    reset();
    setAssetMedia([]);
  };

  const formContent = (
    <ScrollView
      showsHorizontalScrollIndicator={false}
      scrollEnabled
      showsVerticalScrollIndicator={false}
      style={props.styles.contentContainer}
      automaticallyAdjustKeyboardInsets={true}
      keyboardDismissMode="interactive"
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.outerContainer}>
        <View style={styles.baseContainer}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.newAdditionalMedia')}
            </Text>
          </View>
          <View>
            <HorizontalMediaGallery
              onMediaAdded={addMedia}
              onMediaDeleted={deleteMedia}
              mediaPicker={MediaPicker as MediaPickerComponent}
              onUploadingChanged={setUploading}
              canEditMedia={true}
              acceptedMediaTypes={[MediaType.Image]}
            />
          </View>
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.assetName')}
            </Text>
          </View>
          <ControlledTextInput
            testID="add-asset-name"
            control={control}
            placeholder={t('asset.namePlaceholder')}
            name="name"
            backgroundColor={Colors.neutral[200]}
            outlinedTextInput
            disableInnerStyle
            useCropCircle
            hideBorder
          />
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.secondaryDescription')}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            placeholder={t('asset.descriptionPlaceholder')}
            name="description"
            backgroundColor={Colors.neutral[200]}
            outlinedTextInput
            disableInnerStyle
            useCropCircle
            hideBorder
          />
        </View>
        <View style={[styles.baseContainer, styles.formItem]}>
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.details')}
            </Text>
          </View>
          <ControlledTextInput
            control={control}
            placeholder={t('asset.detailsPlaceHolder')}
            name="details"
            textArea={true}
            backgroundColor={Colors.neutral[200]}
            outlinedTextInput
            disableInnerStyle
            useCropCircle
            hideBorder
          />
        </View>
        <View
          style={[styles.baseContainer, styles.lastFormItem]}
        >
          <View>
            <Text style={formStyles.formItemTitle}>
              {t('asset.groups')}
            </Text>
          </View>
          <GroupSelector
            selectedGroups={selectedGroups}
            availableGroups={groups}
            onGroupsChange={setSelectedGroups}
            onCreateGroup={createGroup}
            disabled={false}
            useCropCircle={isCropCircleRedesignEnabled}
            onSelectorEdit={props.onSelectorEdit || slideLayout?.toggleThirdPanel}
          />
        </View>
        {tagCode && (
          <View
            style={[
              styles.baseContainer,
              formStyles.formItem,
              styles.formItemBottom,
            ]}
          >
            <View>
              <Text style={formStyles.formItemTitle}>
                {`5. ${t('asset.tagCode')}`}
              </Text>
            </View>
            <ControlledTextInput
              control={control}
              name="tagCode"
              outlinedTextInput
              editable={false}
              disableInnerStyle={true}
              textColor={'#8E8E93'}
              backgroundColor={'#E9E9EF'}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );

  if (props.bottomBar) {
    return (
      <KeyboardSafeView style={[props.styles.container]}>
        <SaveFormBottom
          isDirty={isDirty && !isLoading}
          isValid={canSave}
          onSubmit={async () => {
            await handleSubmit(onSubmit)();
          }}
          bottomBar={props.bottomBar}
          onResetForm={onResetForm}
          onCancel={props.onCancel}
        >
          {formContent}
        </SaveFormBottom>
      </KeyboardSafeView>
    );
  }

  return (
    <>
      <ScrollView style={[props.styles.container]}>
        {formContent}
      </ScrollView>
      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.footerSaveButton, !canSave && styles.footerSaveButtonDisabled]}
          onPress={canSave ? handleSubmit(onSubmit) : undefined}
          disabled={!canSave}
        >
          <Text variant="bodyLarge" style={styles.footerSaveButtonText}>
            {t('asset.save')}
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );
};

const createAsset = `
  mutation Asset($input: AddAssetInput!) {
    asset {
      addAsset(input: $input) {
        id
      }
    }
  }
`;

const styles = StyleSheet.create({
  outerContainer: {
    paddingBottom: 200,
    backgroundColor: Colors.white
  },
  baseContainer: {
    paddingVertical: tokens.spacing.md,
    flexDirection: 'column',
    width: '100%',
  },
  coverImage: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  baseButton: {
    width: 64,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    borderRadius: tokens.borderRadius.lg,
    marginHorizontal: Spacing.baseMargin.base,
  },
  cancelButton: {
    marginLeft: 8,
    marginRight: Spacing.baseMargin.marginHorizontal * 1.3,
  },
  contentContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
  },
  formItem: {
    width: '100%',
  },
  lastFormItem: {
    marginBottom: tokens.spacing.xl,
  },
  formItemBottom: {
    marginBottom: 40,
  },
  footer: {
    position: 'absolute',
    left: tokens.spacing.xl,
    right: tokens.spacing.xl,
    bottom: 0,
    paddingVertical: tokens.spacing.xl,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  footerSaveButton: {
    backgroundColor: tokens.colors.neutral[800],
    borderRadius: tokens.borderRadius.sm,
    width: '100%',
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerSaveButtonDisabled: {
    backgroundColor: Colors.grayDivider,
  },
  footerSaveButtonText: {
    color: tokens.colors.brand.white,
    fontWeight: tokens.typography.body.mediumEmphasised.fontWeight,
  },
});
